import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { catchError, filter, map, of, take, timeout } from 'rxjs';
import { CanActivateFn, CanActivateChildFn, Router, ActivatedRouteSnapshot, UrlTree } from '@angular/router';
import { AuthService } from '@/app/core/auth/auth.service';
import { ActiveCompanyService } from '@/app/core/company/active-company.service';
import { PermissionsService } from '@/app/core/permissions/permissions.service';

// Sektörel modül route → gerekli policy listesi (OR mantığı).
// Map order önemli: daha spesifik path ('erp/packages') önce, generic ('erp') sonra.
// Faz 2 sektör-agnostik ekranlar (BOM, commissions, packages, giftcards) hem
// ERP rolünden hem de herhangi bir sektörel modül rolünden erişilebilmeli —
// backend `RequireFazIkiPolicy` attribute'üyle hizalı.
const SECTOR_OR_ERP_VIEW = [
  'erp.view',
  'beauty.view', 'fitness.view', 'hotel.view', 'fnb.view',
  'education.view', 'health.view', 'law.view', 'realestate.view',
];

const ROUTE_POLICY_MAP: Record<string, string[]> = {
  'erp/packages':    SECTOR_OR_ERP_VIEW,
  'erp/giftcards':   SECTOR_OR_ERP_VIEW,
  'erp/commissions': SECTOR_OR_ERP_VIEW,
  'erp/bom':         SECTOR_OR_ERP_VIEW,

  'booking':     ['booking.view'],
  'health':      ['health.view'],
  'law':         ['law.view'],
  'hotel':       ['hotel.view'],
  'beauty':      ['beauty.view'],
  'education':   ['education.view'],
  'fitness':     ['fitness.view'],
  'fnb':         ['fnb.view'],
  'realestate':  ['realestate.view'],
  'crm':         ['crm.view'],
  'erp':         ['erp.view'],
  // Mağaza: iki policy'den biri yeterli. 'erp.view' de kabul ediliyor çünkü
  // ekranlar ERP uçlarını okuyor ve ERP modülü olan kiracı mağazayı da görebilir.
  'ecommerce':   ['ecommerce.view', 'erp.view'],
  'web-builder': ['webbuilder.view'],

  // Melbear zamanlanmış görevler — gözetimsiz yazma demek, kendi kapısı var.
  'melbear/schedules': ['ai.agent.schedule'],

  // Sosyal Medya Asistanı. Hesap bağlama, gönderi ve gelen kutusu AYRI
  // yetkiler; her sayfa kendi kapısını ister.
  'social/accounts': ['social.account.manage'],
  'social/posts':    ['social.post.create'],
  // Takvim zamanlama demek; zamanlamak yayınlamaktır, yalnız ertelenmiştir.
  'social/calendar':  ['social.publish'],
  'social/approvals': ['social.post.approve'],
  'social/inbox':    ['social.inbox.read'],
  'social':          ['social.post.create'],

  // Veri dışa aktarımı — kiracının TÜM verisi tek pakette; modül görüntüleme
  // yetkisi yetmez, kendi kapısı var. Sahip zaten bypass eder.
  'settings/data': ['client.data.export'],

  // Finans alt-sayfaları — modül view yetmez, finance.collect gerekir.
  'clinic/billing': ['clinic.finance.collect'],
  // Prim taksiti tahsilatı para hareketidir; `insurance.view` yetmez.
  // (Mutabakat listesi görüntülenebilir, KAPATMA butonu ekranda ayrıca
  // gizlenir ve asıl kapı sunucudadır.)
  'insurance/collections': ['insurance.finance.collect'],
  // ÇAKIŞMA GÜVENLİĞİ: eşleşme `/<anahtar>` arar ve en UZUN anahtar kazanır.
  // Sigorta modülünün öneki 'insurance' olduğu için '/portal/dental/insurance'
  // ve '/portal/pharmacy/insurance' de onu yakalardı; o ekranlar diş/eczane
  // modülüne ait, sigorta brokerliğiyle ilgisi yok. Daha uzun anahtarlar
  // onları kendi modüllerine bağlar.
  'dental/insurance':   ['dental.view'],
  'pharmacy/insurance': ['pharmacy.view'],

  // Yeni sektörel modüller — owner bypass'a güvenmek yerine policy bazlı da kontrol.
  'site':          ['site.view'],
  'storage':       ['storage.view'],
  'ngo':           ['ngo.view'],
  'religious':     ['rel.view'],
  'consulting':    ['cons.view'],
  'eventorg':      ['evt.view'],
  'homeservices':  ['hs.view'],
  'construction':  ['cns.view'],
  'realty':        ['realty.view'],
  'agency':        ['agency.view'],
  'agriculture':   ['agriculture.view'],
  'automotive':    ['automotive.view'],
  'creator':       ['creator.view'],
  'daycare':       ['daycare.view'],
  'petservices':   ['petservices.view'],
  // Doz hesabi AYRI izin ailesi: protokol duzenlemek klinik guvenlik isidir,
  // `petservices.update` (randevu/fatura) ile ayni yetkiye baglanmamali.
  'petservices/dose': ['dose.view'],
  // Tazminat PARAYLA ilgilidir; `petservices.view` yetmez.
  'petservices/claims': ['claim.view'],
  // Lisans sarti SOZLESMEdir; `creator.update` (icerik duzenleme) yetmez.
  'creator/deal-terms': ['terms.view'],
  // Gece denetimi her folyoya para yazar; `hotelmgmt.update` yetmez.
  'hotelmgmt/night-audit': ['audit.view'],
  // Veli bildirimi modul-agnostik: TEK politika ailesi, sektor basina ayri
  // kod her yeni modulde birinin unutulmasi demek olurdu.
  'daycare/notices': ['notice.view'],
  'edumgmt/notices': ['notice.view'],
  // Denetim formu modul-agnostik: TEK politika ailesi.
  'construction/inspections': ['checklist.view'],
  // Şantiye denetimi ortak kontrol formundan ayrı ekran: modülün kendi yetkisi.
  'construction/site-inspections': ['construction.view'],
  'restaurant/inspections': ['checklist.view'],
  'hotelmgmt/inspections': ['checklist.view'],
  'automotive/inspections': ['checklist.view'],
  // Aynı aile, yeni bağlanan sektörler. Modülün kendi view yetkisi BURADA
  // yetmez: denetim formu imzalanınca müfettişe gösterilen belge oluyor.
  // ('site/inspections' ile 'construction/site-inspections' karışmaz: eşleşme
  // '/site/inspections' arar, oradaki segment 'site-inspections'.)
  'daycare/inspections': ['checklist.view'],
  'petservices/inspections': ['checklist.view'],
  'site/inspections': ['checklist.view'],
  'storage/inspections': ['checklist.view'],
  'agriculture/inspections': ['checklist.view'],
  'clinic/inspections': ['checklist.view'],
  'dental/inspections': ['checklist.view'],
  // Ekspertiz, denetim formundan ayrı ekran: modülün kendi yetkisiyle açılır.
  'automotive/appraisals': ['automotive.view'],
  // Karne YAYIMLAMAK veliye soz vermektir; `edumgmt.update` yetmez.
  'edumgmt/report-cards': ['card.view'],
  // Masa oturumu acmak QR'i siparise ACAR; `restaurant.update` yetmez.
  'restaurant/qr': ['qr.view'],
  'restaurant':    ['restaurant.view'],
  'hotelmgmt':     ['hotelmgmt.view'],
  'edumgmt':       ['edumgmt.view'],
  'lawmgmt':       ['lawmgmt.view'],
  'immigration':   ['immigration.view'],
  'clinic':        ['clinic.view'],
  'pharmacy':      ['pharmacy.view'],
  'dental':        ['dental.view'],
  'logistics':     ['logistics.view'],
  'sportsclub':    ['sportsclub.view'],
  'insurance':     ['insurance.view'],
  // İnsan Kaynakları & İşe Alım. Rota öneki 'hr', backend ucu 'api/hrm'.
  // Ortak özlük ekranı (/portal/<sektor>/staff-hr) BAŞKA bir yüzeydir;
  // burada eşleşme '/hr' segmentine bakar, onunla karışmaz.
  'hr':            ['hr.view'],
};

// Route prefix → aktif şirkette yüklü olması beklenen modül kodu
// (PortalPermissionsManager.ActiveCompanyModules = ClientModule.ModuleCode).
// Owner her policy'i bypass eder; modül gerçekten aktif değilse menü
// gizli olsa da elle URL ile sayfaya girilmemeli (örn. Bloor Walk-in Clinic
// yüklü, kullanıcı /portal/fitness/check-in yazıyor → engelle).
//
// NOT — modül kodu ≠ route prefix olan istisnalar:
//   - 'web-builder' route → modül kodu 'webbuilder'
//   - ERP Faz 2 sektör-agnostik ekranları (erp/packages, erp/giftcards,
//     erp/commissions, erp/bom) KASITEN burada YOK — onlar beauty/fitness
//     gibi sektör kullanıcılarına da açık (ROUTE_POLICY_MAP'teki
//     SECTOR_OR_ERP_VIEW ile korunur). Sadece saf ERP ekranları 'erp'
//     modülünü ister.
// Değer dizi olabilir: aynı ekran birden çok modül koduyla satılıyorsa
// (e-ticaret hem tam paketle hem eklentiyle geliyor) hepsi kabul edilmeli.
const ROUTE_MODULE_MAP: Record<string, string | string[]> = {
  beauty:       'beauty',
  fitness:      'fitness',
  hotel:        'hotel',
  fnb:          'fnb',
  education:    'education',
  health:       'health',
  law:          'law',
  realestate:   'realestate',
  site:         'site',
  storage:      'storage',
  ngo:          'ngo',
  religious:    'religious',
  consulting:   'consulting',
  eventorg:     'eventorg',
  homeservices: 'homeservices',
  construction: 'construction',
  realty:       'realty',
  agency:       'agency',
  agriculture:  'agriculture',
  automotive:   'automotive',
  creator:      'creator',
  daycare:      'daycare',
  petservices:  'petservices',
  restaurant:   'restaurant',
  hotelmgmt:    'hotelmgmt',
  edumgmt:      'edumgmt',
  lawmgmt:      'lawmgmt',
  immigration:  'immigration',
  clinic:       'clinic',
  pharmacy:     'pharmacy',
  dental:       'dental',
  logistics:    'logistics',
  sportsclub:   'sportsclub',
  insurance:    'insurance',
  // Diş/eczane modüllerinin KENDİ sigorta ekranları; 'insurance' öneki onları
  // da yakalamasın diye daha uzun anahtarla kendi modüllerine bağlanıyorlar.
  'dental/insurance':   'dental',
  'pharmacy/insurance': 'pharmacy',
  // İnsan Kaynakları & İşe Alım. ('/hr' araması '/portal/<sektor>/staff-hr'
  // adreslerini YAKALAMAZ — orada segment 'staff-hr', önünde '/' yok.)
  hr:           'hr',

  // Çekirdek/yatay modüller — yüklü değilse sayfaları da kapalı.
  crm:           'crm',
  booking:       'booking',
  'web-builder': 'webbuilder',

  // Sosyal Medya Asistanı satın alınmadıysa sayfalar kapalı. Bu kapı owner'ı
  // da bypass ettirmez — asistan ayrı bir abonelik (AI_SOCIAL_ASSIST).
  social: 'social',

  // Mağaza ekranları 'ecommerce' modülünü ister. Bu kapı owner'ı da bypass
  // ettirmez; e-ticaret kiracısına verilen erp.* policy'leri /portal/erp/*
  // ekranlarını AÇMAZ çünkü orada 'erp' modül kodu aranıyor.
  // ECOMMERCE_ADDON kiracıya 'webbuilder_ecommerce' yazıyor, tam paket
  // 'ecommerce'. Menü ikisini de kabul ediyordu ama bu kapı yalnız birini
  // tanıyordu: eklentiyle mağaza alan kiracı menüyü görüp tıklayınca panoya
  // düşüyordu.
  ecommerce: ['ecommerce', 'webbuilder_ecommerce'],

  // Saf ERP ekranları 'erp' modülünü ister (Faz 2 paylaşımlı ekranlar hariç).
  'erp/products': 'erp',
  'erp/orders':   'erp',
  'erp/invoices': 'erp',
  'erp/stock':    'erp',
  'erp/payments': 'erp',
};

// ─── Admin guard ─────────────────────────────────────────────────────────────
export const authGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  if (authService.isAuthenticated()) return true;
  return router.createUrlTree(['/auth/sign-in']);
};

// ─── Guest guard ─────────────────────────────────────────────────────────────
export const guestGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  if (authService.isAuthenticated()) return router.createUrlTree(['/admin/dashboards/melland']);
  return true;
};

// ─── Portal guard ─────────────────────────────────────────────────────────────
// 1. Login değilse → /auth/sign-in
// 2. Platform admin ise → /admin
// 3. Route'un gerektirdiği policy yoksa → /portal/dashboard (403)
//    Policy kaynağı: PermissionsService (JWT'den bağımsız, dinamik)
export const portalGuard: CanActivateFn = (_route: ActivatedRouteSnapshot, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.isAuthenticated()) {
    return router.createUrlTree(['/auth/sign-in']);
  }

  if (authService.isPlatformAdmin()) {
    return router.createUrlTree(['/admin/dashboards/melland']);
  }

  // Hedef URL'i state'ten al — parent canActivate'te route.pathFromRoot child
  // segmentlerini içermez; state.url tam hedef yolu verir (query/fragment hariç).
  const fullUrl = state.url.split('?')[0].split('#')[0];

  // Daha spesifik path'i önce eşle (erp/packages → erp/packages, erp/products → erp).
  const matchKey = Object.keys(ROUTE_POLICY_MAP)
    .sort((a, b) => b.length - a.length)
    .find(seg => fullUrl.includes(`/${seg}`));

  // Sektörel modül kontrolü — ROUTE_POLICY_MAP'te değil ROUTE_MODULE_MAP'te
  // anahtar bulunsa bile gating bağımsız çalışmalı.
  const moduleKey = Object.keys(ROUTE_MODULE_MAP)
    .sort((a, b) => b.length - a.length)
    .find(seg => fullUrl.includes(`/${seg}`));

  if (matchKey || moduleKey) {
    const companySvc = inject(ActiveCompanyService);
    const permissionsSvc = inject(PermissionsService);

    // Şirket seçilmemiş → dashboard'a yönlendir
    if (!companySvc.activeCompanyId()) {
      return router.createUrlTree(['/portal/dashboard']);
    }

    // YETKİLER YÜKLENMEDEN KARAR VERİLMEZ. Önceden aşağıdaki iki kapı da
    // `loaded()` şartına bağlıydı; sayfa URL ile doğrudan açılınca (tam yükleme)
    // yetkiler henüz gelmemiş oluyor ve kapı ATLANIYORDU — Poliklinik şirketinde
    // satın alınmamış /portal/hr açıldı (2026-09-21). Artık yanıt beklenir; istek
    // hiç atılmadıysa guard kendisi başlatır. 8 sn içinde gelmezse erişim yok —
    // asıl koruma sunucuda (RequirePolicy modül kapısı), burası yalnız ekran.
    if (!permissionsSvc.loaded()) {
      if (!permissionsSvc.loading()) permissionsSvc.refresh(companySvc.activeCompanyId());
      return toObservable(permissionsSvc.loaded).pipe(
        filter(Boolean),
        take(1),
        timeout(8000),
        map(() => kapiKarari(permissionsSvc, router, matchKey, moduleKey)),
        catchError(() => of(router.createUrlTree(['/portal/dashboard']))),
      );
    }
    return kapiKarari(permissionsSvc, router, matchKey, moduleKey);
  }

  return true;
};

/** Modül + policy kapısı — yetkiler YÜKLENMİŞKEN çağrılır. */
function kapiKarari(
  permissionsSvc: PermissionsService,
  router: Router,
  matchKey: string | undefined,
  moduleKey: string | undefined,
): true | UrlTree {
    // Modül-bazlı kapı: yüklenmemiş sektörel modüle URL ile elle girilmesin.
    // Owner her policy'i bypass eder ama bu kapı kasten owner-agnostic —
    // "şirket bu modülü gerçekten satın aldı mı?" sorusuna cevap verir.
    if (moduleKey) {
      const tanim = ROUTE_MODULE_MAP[moduleKey];
      const kodlar = Array.isArray(tanim) ? tanim : [tanim];
      if (!kodlar.some((kod) => permissionsSvc.hasModule(kod))) {
        return router.createUrlTree(['/portal/dashboard']);
      }
    }

    // Policy kapısı — owner her policy'i bypass eder ([[feedback_owner_bypass]]);
    // değilse listedeki en az bir policy gerekir, yoksa → dashboard.
    if (matchKey && !permissionsSvc.isOwner()) {
      const requiredPolicies = ROUTE_POLICY_MAP[matchKey];
      if (!requiredPolicies.some(p => permissionsSvc.hasNow(p))) {
        return router.createUrlTree(['/portal/dashboard']);
      }
    }

  return true;
}

// canActivateChild varyantı — parent `canActivate` yalnız ilk girişte çalışır;
// SPA içi alt-route geçişlerinde modül/policy kapısının çalışması için her
// child navigasyonunda tetiklenmeli. portalGuard ile aynı mantık (imza uyumlu).
export const portalChildGuard: CanActivateChildFn = (childRoute, state) =>
  (portalGuard as CanActivateChildFn)(childRoute, state);
